import React from 'react';
import { Dialog } from '@headlessui/react';

interface BaseModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: React.ReactNode;
    children: React.ReactNode;
    actions?: React.ReactNode;
    maxWidth?: string;
    /** Quando false, o modal ocupa só a altura do conteúdo (até 90vh) em vez de sempre 90vh. Default true mantém o comportamento atual. */
    fullHeight?: boolean;
    /** Quando false, remove o botão "X" e desliga o fechamento por clique fora/Esc — usado
     * quando uma resposta explícita é obrigatória. Default true mantém o comportamento atual. */
    dismissible?: boolean;
    /** Botões extras no cabeçalho, à esquerda do "X" (ex.: baixar PDF). Opcional — quem não
     * passa nada continua igual. */
    headerActions?: React.ReactNode;
}

const BaseModal: React.FC<BaseModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    actions,
    maxWidth = 'max-w-5xl',
    fullHeight = true,
    dismissible = true,
    headerActions,
}) => {
    if (!isOpen) return null;

    return (
        <Dialog open={isOpen} onClose={dismissible ? onClose : () => {}} className="relative z-50">
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className={`relative w-full ${maxWidth} transform rounded-lg bg-white text-left shadow-xl transition-all flex flex-col ${fullHeight ? 'h-[90vh]' : 'max-h-[90vh]'} overflow-hidden`}>
                    <div className="p-6 border-b border-gray-200 flex-shrink-0 flex justify-between items-start">
                        <Dialog.Title as="div" className="text-lg font-medium leading-6 text-gray-900">
                            {title}
                        </Dialog.Title>
                        <div className="flex items-center gap-3">
                            {headerActions}
                            {dismissible && (
                                <button type="button" className="text-gray-400 hover:text-gray-500" onClick={onClose}>
                                    <span className="sr-only">Fechar</span>
                                    <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>

                    <div className={`px-6 ${fullHeight ? 'flex-grow' : ''} overflow-y-auto`}>
                        {children}
                    </div>

                    {actions && (
                        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex-shrink-0">
                            {actions}
                        </div>
                    )}
                </Dialog.Panel>
            </div>
        </Dialog>
    );
};

export default BaseModal; 